import React from 'react'
import { Modal, Table, Checkbox, Tooltip, Statistic, Row, Col, Spin, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { CalcDetailData, PeriodRow, IndicatorType } from '@/api/recviz'

const { Text } = Typography

interface CalculationDetailModalProps {
  visible: boolean
  onClose: () => void
  departmentName: string
  indicatorType: IndicatorType
  indicatorLabel: string
  data: CalcDetailData | null
  onPeriodToggle: (period: string, included: boolean) => void
  isToggling?: boolean
}

const CalculationDetailModal: React.FC<CalculationDetailModalProps> = ({
  visible,
  onClose,
  departmentName,
  indicatorType: _indicatorType,
  indicatorLabel,
  data,
  onPeriodToggle,
  isToggling = false,
}) => {
  // Определяем стиль строки в зависимости от статуса
  const getRowClassName = (record: PeriodRow): string => {
    if (!record.included && record.excluded_reason) {
      // Исключён алгоритмом
      return 'excluded-by-algorithm'
    }
    if (record.included && record.is_manual_toggle && record.manual_warning) {
      // Включён вручную (был невалидным)
      return 'included-manually'
    }
    if (!record.included && record.is_manual_toggle) {
      // Исключён вручную
      return 'excluded-manually'
    }
    return ''
  }

  // Колонки таблицы
  const columns: ColumnsType<PeriodRow> = [
    {
      title: '',
      key: 'checkbox',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={record.included}
          onChange={(e) => onPeriodToggle(record.period, e.target.checked)}
          disabled={isToggling}
        />
      ),
    },
    {
      title: 'Период',
      dataIndex: 'label',
      key: 'label',
      width: 150,
    },
    {
      title: 'Случаев',
      dataIndex: 'cases',
      key: 'cases',
      width: 100,
      align: 'right',
    },
    {
      title: 'Значение',
      dataIndex: 'value',
      key: 'value',
      width: 120,
      align: 'right',
      render: (value: number) => value.toFixed(3),
    },
    {
      title: 'Статус',
      key: 'status',
      render: (_, record) => {
        if (record.manual_warning) {
          return (
            <Tooltip title={record.manual_warning}>
              <Text type="warning">
                <InfoCircleOutlined /> Включён вручную
              </Text>
            </Tooltip>
          )
        }
        if (record.excluded_reason) {
          return (
            <Text type="secondary" delete={!record.included}>
              ⚠ Исключён: {record.excluded_reason}
            </Text>
          )
        }
        if (!record.included && record.is_manual_toggle) {
          return <Text type="secondary">Исключён вручную</Text>
        }
        return <Text>—</Text>
      },
    },
  ]

  return (
    <Modal
      title={`Детализация расчёта: ${indicatorLabel} — ${departmentName}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Spin spinning={isToggling || !data}>
        {data && (
          <>
            {/* Таблица периодов */}
            <Table
              columns={columns}
              dataSource={data.periods}
              rowKey="period"
              pagination={false}
              size="small"
              rowClassName={getRowClassName}
              style={{ marginBottom: 24 }}
            />

            {/* Блок итогов */}
            <div
              style={{
                background: '#f5f5f5',
                padding: '16px',
                borderRadius: '4px',
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Среднегодовой"
                    value={data.annual_avg}
                    precision={3}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Лучший месяц"
                    value={data.max_monthly_value}
                    precision={3}
                    valueStyle={{ fontSize: '16px' }}
                    suffix={
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        ({data.periods.find(p => p.period === data.max_monthly_period)?.label || data.max_monthly_period})
                      </Text>
                    }
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Среднемаксимальный"
                    value={data.avg_max}
                    precision={3}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Областной бенчмарк"
                    value={data.benchmark ?? 0}
                    precision={3}
                    valueStyle={{ fontSize: '16px' }}
                    suffix={!data.benchmark ? <Text type="secondary">(нет данных)</Text> : undefined}
                  />
                </Col>
                <Col span={24}>
                  <Statistic
                    title="Плановое значение"
                    value={data.planned_value}
                    precision={3}
                    valueStyle={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}
                    suffix={
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (источник: {data.planned_source === 'our' ? 'собственный' : 'среднее'})
                      </Text>
                    }
                  />
                </Col>
              </Row>
            </div>
          </>
        )}
      </Spin>

      <style>{`
        .excluded-by-algorithm {
          text-decoration: line-through;
          background-color: #f5f5f5;
          color: #8c8c8c;
        }
        .included-manually {
          background-color: #fffbe6;
        }
        .excluded-manually {
          text-decoration: line-through;
          color: #8c8c8c;
        }
      `}</style>
    </Modal>
  )
}

export default CalculationDetailModal
